import { Box, Typography, IconButton, Button, Tooltip, useTheme, alpha, Rating } from '@mui/material'
import { motion } from 'framer-motion'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { DisplayCurrency } from 'components'

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)

const COLOR_MAP = {
  r: '#EF4444', b: '#3B82F6', g: '#22C55E', y: '#EAB308', o: '#F97316', p: '#A855F7',
  k: '#111827', w: '#F9FAFB',
  red: '#EF4444', blue: '#3B82F6', green: '#22C55E', yellow: '#EAB308',
  orange: '#F97316', purple: '#A855F7', pink: '#EC4899', black: '#111827',
  white: '#F9FAFB', gray: '#9CA3AF', grey: '#9CA3AF', navy: '#1E3A5F',
  teal: '#0D9488', brown: '#92400E', gold: '#F59E0B', silver: '#CBD5E1',
  coral: '#F87171', indigo: '#6366F1', rose: '#FB7185', emerald: '#10B981',
  violet: '#7C3AED', maroon: '#7F1D1D', cyan: '#06B6D4', lime: '#84CC16',
}

const ColorDot = ({ color }) => {
  const name = typeof color === 'string' ? color : color.name
  const hexDb = typeof color === 'string' ? null : color.color_code
  const css = hexDb || COLOR_MAP[name?.toLowerCase()] ||
    (/^#[0-9a-f]{3,8}$/i.test(name ?? '') ? name : null)
  const isLight = css && /^#[fFeEdDcC]{3,6}/.test(css)

  return (
    <Tooltip title={name} placement="top" arrow>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: css || 'primary.main',
          border: (theme) => `1.5px solid ${isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.25)}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          cursor: 'default',
          transition: 'transform 0.15s',
          '&:hover': { transform: 'scale(1.35)' },
          ...(!css && { background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` }),
        }}
      />
    </Tooltip>
  )
}

const PremiumProductCard = ({
  product,
  index = 0,
  layout = 'grid',
  quickView = false,
  rankBadge,
  newBadge = false,
  featuredBadge = Boolean(product?.isFeatured ?? product?.is_featured),
  onNavigate,
  onQuickView,
  onAddToCart,
  onRemoveFromCart,
  onToggleWishlist,
  inCart = false,
  cartQty = 0,
  isFav = false,
  finalPrice,
}) => {
  const t = useTheme()
  const isDark = t.palette.mode === 'dark'
  const isList = layout === 'list'

  const hasVariants = !!(product?.hasVariants ?? product?.has_variants)
  const discPct = Number(product.discountPercentage || 0)
  const variantColors = (product.variantColors || []).slice(0, 5)
  const variantSizes = (product.variantSizes || []).slice(0, 4)
  const ratingValue = Number(product?.rating || 0)
  const reviewCount = Number(product?.reviewCount || 0)
  const secondaryImage = product?.secondaryImage || product?.secondary_image || product?.hover_image || null
  const metaLabel = product?.brand || product?.category_name || product?.category || ''

  const minP = product.variantMinPrice
  const maxP = product.variantMaxPrice
  const priceRangeLabel = hasVariants && minP != null
    ? (minP === maxP ? fmt(minP) : `${fmt(minP)} – ${fmt(maxP)}`)
    : null

  const rankColor = rankBadge === '#1' ? '#F59E0B'
    : rankBadge === '#2' ? '#94A3B8'
    : rankBadge === '#3' ? '#B45309'
    : t.palette.primary.main

  const actionButtonSx = {
    height: 32,
    borderRadius: '4px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
    px: 1.2,
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ height: '100%' }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '8px',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: isList ? { xs: 'column', sm: 'row' } : 'column',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isDark ? alpha(t.palette.common.white, 0.06) : 'rgba(0, 0, 0, 0.08)',
          boxShadow: 'none',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            boxShadow: isDark
              ? '0 4px 12px rgba(0,0,0,0.3)'
              : '0 2px 8px rgba(0,0,0,0.12)',
            borderColor: alpha(t.palette.divider, 0.3),
          },
          '&:hover .pm-img': { transform: 'scale(1.04)' },
          '&:hover .pm-img-secondary': { opacity: secondaryImage ? 1 : 0 },
          '&:hover .pm-fav': { opacity: 1 },
        }}
      >
        <Box
          onClick={onNavigate}
          sx={{
            position: 'relative',
            width: isList ? { xs: '100%', sm: '35%' } : '100%',
            minWidth: isList ? { sm: 200 } : '100%',
            pt: isList ? { xs: '100%', sm: 0 } : '100%',
            minHeight: isList ? { sm: 260 } : 0,
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
            bgcolor: isDark
              ? alpha(t.palette.background.elevated || t.palette.background.paper, 0.4)
              : '#f5f5f5',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Box
            component="img"
            src={product.thumbnail || '/images/picture-loading-failed.svg'}
            alt={product.title}
            className="pm-img"
            onError={(e) => { e.target.src = '/images/picture-loading-failed.svg'; e.target.onerror = null }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s cubic-bezier(.25,.46,.45,.94)',
            }}
          />

          {secondaryImage && secondaryImage !== product.thumbnail && (
            <Box
              component="img"
              src={secondaryImage}
              alt={`${product.title} alternate view`}
              className="pm-img-secondary"
              onError={(e) => { e.target.style.display = 'none' }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0,
                transition: 'opacity 0.3s ease',
              }}
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.4,
            }}
          >
            {rankBadge && (
              <Box
                sx={{
                  bgcolor: rankColor,
                  color: '#fff',
                  borderRadius: '3px',
                  px: 0.6,
                  py: 0.2,
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                {rankBadge}
              </Box>
            )}
            {newBadge && (
              <Box
                sx={{
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                  borderRadius: '3px',
                  px: 0.6,
                  py: 0.2,
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                NEW
              </Box>
            )}
            {featuredBadge && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                  color: '#000',
                  borderRadius: '3px',
                  px: 0.6,
                  py: 0.2,
                  fontSize: '0.55rem',
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                FEATURED
              </Box>
            )}
            {discPct > 0 && (
              <Box
                sx={{
                  bgcolor: '#388e3c',
                  color: '#fff',
                  borderRadius: '3px',
                  px: 0.6,
                  py: 0.2,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                }}
              >
                {discPct.toFixed(0)}% off
              </Box>
            )}
          </Box>

          <IconButton
            className="pm-fav"
            size="small"
            onClick={(e) => { e.stopPropagation(); onToggleWishlist() }}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              zIndex: 4,
              opacity: { xs: 1, md: 0 },
              transition: 'opacity 0.2s',
              bgcolor: alpha('#fff', 0.95),
              border: '1px solid',
              borderColor: 'divider',
              width: 30,
              height: 30,
              '&:hover': {
                bgcolor: '#fff',
              },
            }}
          >
            {isFav
              ? <FavoriteRoundedIcon sx={{ fontSize: 16, color: 'error.main' }} />
              : <FavoriteBorderRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          </IconButton>
        </Box>

        <Box
          sx={{
            p: isList ? { xs: 1.2, sm: 1.8 } : { xs: 0.8, sm: 1 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: isList ? 200 : 140,
          }}
        >
          {metaLabel && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                mb: 0.4,
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              {metaLabel}
            </Typography>
          )}

          <Typography
            onClick={onNavigate}
            sx={{
              fontSize: isList ? { xs: '0.85rem', sm: '0.95rem' } : { xs: '0.75rem', sm: '0.8rem' },
              fontWeight: 500,
              color: 'text.primary',
              cursor: 'pointer',
              lineHeight: 1.3,
              mb: 0.5,
              transition: 'color 0.15s',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: isList ? '2.4em' : '2.2em',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {product.title}
          </Typography>

          {(ratingValue > 0 || reviewCount > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.6 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3,
                  bgcolor: '#388e3c',
                  color: '#fff',
                  px: 0.5,
                  py: 0.15,
                  borderRadius: '4px',
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                  {ratingValue > 0 ? ratingValue.toFixed(1) : 'New'}
                </Typography>
                <StarRoundedIcon sx={{ fontSize: 10 }} />
              </Box>
              {reviewCount > 0 && (
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  ({reviewCount.toLocaleString('en-IN')})
                </Typography>
              )}
            </Box>
          )}

          {/* Show star rating when there's a rating but no review count */}
          {ratingValue > 0 && reviewCount === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, mb: 0.6 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <StarRoundedIcon
                  key={i}
                  sx={{
                    fontSize: 12,
                    color: i < Math.floor(ratingValue) ? '#388e3c' : '#ccc',
                  }}
                />
              ))}
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 0.5 }}>
                {ratingValue.toFixed(1)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.6, mb: 0.8 }}>
            {hasVariants && priceRangeLabel ? (
              <Typography
                sx={{
                  fontSize: isList ? { xs: '0.95rem', sm: '1.1rem' } : { xs: '0.85rem', sm: '0.95rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1,
                }}
              >
                {priceRangeLabel}
              </Typography>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: isList ? { xs: '0.95rem', sm: '1.1rem' } : { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1,
                  }}
                >
                  <DisplayCurrency number={finalPrice} />
                </Typography>
                {discPct > 0 && (
                  <>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 400,
                        lineHeight: 1,
                        color: 'text.disabled',
                        textDecoration: 'line-through',
                      }}
                    >
                      <DisplayCurrency number={product.originalPrice} />
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: '#388e3c',
                      }}
                    >
                      {discPct}% off
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>

          <Box sx={{ minHeight: 20, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.8 }}>
            {variantColors.slice(0, 4).map((c) => (
              <ColorDot key={typeof c === 'string' ? c : c.name} color={c} />
            ))}
            {variantColors.length > 0 && (product.variantColors?.length || 0) > 4 && (
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary' }}>
                +{product.variantColors.length - 4}
              </Typography>
            )}
            {variantSizes.slice(0, 3).map((s) => (
              <Box
                key={s}
                sx={{
                  px: 0.5,
                  borderRadius: '3px',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}
              >
                {s}
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {hasVariants ? (
              <Button
                onClick={onNavigate}
                sx={{
                  ...actionButtonSx,
                  flex: 1,
                  color: 'primary.main',
                  bgcolor: alpha(t.palette.primary.main, 0.08),
                  border: '1px solid',
                  borderColor: alpha(t.palette.primary.main, 0.2),
                  '&:hover': {
                    bgcolor: alpha(t.palette.primary.main, 0.12),
                  },
                }}
              >
                View Options
              </Button>
            ) : inCart ? (
              <Box
                display="flex"
                alignItems="center"
                gap={0.4}
                sx={{
                  height: 32,
                  flex: 1,
                  bgcolor: alpha(t.palette.primary.main, 0.08),
                  border: '1px solid',
                  borderColor: alpha(t.palette.primary.main, 0.2),
                  borderRadius: '4px',
                  px: 0.5,
                }}
              >
                <IconButton
                  size="small"
                  onClick={onRemoveFromCart}
                  sx={{
                    color: 'primary.main',
                    width: 24,
                    height: 24,
                    '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.15) },
                  }}
                >
                  <RemoveRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: 'text.primary',
                    minWidth: 24,
                    textAlign: 'center',
                  }}
                >
                  {cartQty}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onAddToCart}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    width: 24,
                    height: 24,
                    borderRadius: '4px',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <AddRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={onAddToCart}
                sx={{
                  ...actionButtonSx,
                  flex: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                Add to Cart
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </motion.div>
  )
}

export const PremiumProductCardSkeleton = () => (
  <Box
    sx={{
      borderRadius: '24px',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: (theme) => theme.palette.mode === 'dark'
        ? '0 4px 24px rgba(0,0,0,0.45)'
        : '0 2px 12px rgba(0,0,0,0.07)',
    }}
  >
    <Box
      sx={{
        pt: '76%',
        position: 'relative',
        bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.elevated || '#161E35' : theme.palette.grey[100],
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          '@keyframes pm-shimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' },
          },
          background: (theme) => `linear-gradient(90deg,
            transparent 25%,
            ${alpha(theme.palette.text.primary, 0.05)} 50%,
            transparent 75%)`,
          backgroundSize: '200% 100%',
          animation: 'pm-shimmer 1.6s linear infinite',
        }}
      />
    </Box>
    <Box sx={{ p: '16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {[['38%', 12], ['78%', 18], ['55%', 12], ['45%', 18], ['100%', 40]].map(([w, h], i) => (
        <Box
          key={i}
          sx={{
            width: w,
            height: h,
            borderRadius: '8px',
            bgcolor: (theme) => alpha(theme.palette.text.primary, i === 4 ? 0.07 : 0.05),
          }}
        />
      ))}
    </Box>
  </Box>
)

export default PremiumProductCard
